import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiEdit2, FiShield, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { tenantAPI } from '../../services/api';
import DataTable from '../../components/shared/DataTable';
import Modal from '../../components/shared/Modal';

const TenantListPage = () => {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [search, setSearch] = useState('');
  
  // Formulaire de création
  const [form, setForm] = useState({ 
    first_name: '', last_name: '', email: '', phone: '', address: '', occupation: '', employer: '', monthly_income: '', id_type: '', id_number: '', emergency_contact_name: '', emergency_contact_phone: '', notes: '',
    create_account: false, password: ''
  });

  // Formulaire d'édition
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', address: '', occupation: '', employer: '', monthly_income: '', id_type: '', id_number: '', emergency_contact_name: '', emergency_contact_phone: '', notes: '', is_active: true,
    has_portal: false, enable_portal_access: false, portal_active: true, password: ''
  });

  const navigate = useNavigate();

  const fetchTenants = () => tenantAPI.list({ search })
    .then(r => { setTenants(r.data); setLoading(false); })
    .catch(() => setLoading(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTenants(); }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.monthly_income) payload.monthly_income = parseFloat(payload.monthly_income);
      else delete payload.monthly_income;
      
      if (form.create_account) {
        await tenantAPI.createWithAccount(payload);
      } else {
        await tenantAPI.create(payload);
      }
      setShowModal(false);
      setForm({ 
        first_name: '', last_name: '', email: '', phone: '', address: '', occupation: '', employer: '', monthly_income: '', id_type: '', id_number: '', emergency_contact_name: '', emergency_contact_phone: '', notes: '',
        create_account: false, password: ''
      });
      fetchTenants();
    } catch (err) { alert(err.response?.data?.error || 'Erreur lors de la création du locataire'); }
  };

  const openEdit = (tenant, e) => {
    if (e) e.stopPropagation();
    setSelectedTenant(tenant);
    setEditForm({
      first_name: tenant.first_name || '',
      last_name: tenant.last_name || '',
      email: tenant.email || '',
      phone: tenant.phone || '',
      address: tenant.address || '',
      occupation: tenant.occupation || '',
      employer: tenant.employer || '',
      monthly_income: tenant.monthly_income != null ? String(tenant.monthly_income) : '',
      id_type: tenant.id_type || '',
      id_number: tenant.id_number || '',
      emergency_contact_name: tenant.emergency_contact_name || '',
      emergency_contact_phone: tenant.emergency_contact_phone || '',
      notes: tenant.notes || '',
      is_active: tenant.is_active !== false,
      has_portal: Boolean(tenant.user_id),
      enable_portal_access: false,
      portal_active: tenant.user_is_active !== false,
      password: '',
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setEditLoading(true);
    try {
      const payload = {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        email: editForm.email,
        phone: editForm.phone,
        address: editForm.address,
        occupation: editForm.occupation,
        employer: editForm.employer,
        monthly_income: editForm.monthly_income ? parseFloat(editForm.monthly_income) : null,
        id_type: editForm.id_type,
        id_number: editForm.id_number,
        emergency_contact_name: editForm.emergency_contact_name,
        emergency_contact_phone: editForm.emergency_contact_phone,
        notes: editForm.notes,
        is_active: editForm.is_active,
        // Paramètres portail
        enable_portal_access: editForm.enable_portal_access,
        portal_active: editForm.portal_active,
      };

      if (editForm.password && editForm.password.trim().length > 0) {
        payload.password = editForm.password.trim();
      }

      await tenantAPI.update(selectedTenant.id, payload);
      setShowEditModal(false);
      fetchTenants();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la mise à jour du locataire');
    } finally {
      setEditLoading(false);
    }
  };

  const columns = [
    { header: 'Nom', render: (r) => <strong>{r.first_name} {r.last_name}</strong> },
    { header: 'Téléphone', accessor: 'phone' },
    { header: 'Email', accessor: 'email' },
    { header: 'Profession', accessor: 'occupation' },
    { header: 'Contrats actifs', accessor: 'active_contracts' },
    { 
      header: 'Accès Portail', 
      render: (r) => {
        if (!r.user_id) {
          return <span className="badge badge-secondary" style={{ fontSize: 11 }}>Non activé</span>;
        }
        if (r.user_is_active === false) {
          return <span className="badge badge-warning" style={{ fontSize: 11 }}>Suspendu</span>;
        }
        return <span className="badge badge-success" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}><FiCheckCircle size={11} /> Actif</span>;
      }
    },
    { header: 'Impayés', render: (r) => r.overdue_payments > 0 ? <span className="badge badge-danger">{r.overdue_payments}</span> : <span className="badge badge-success">0</span> },
    { header: 'Solvabilité', render: (r) => r.solvability_score !== null ? <span className={`badge ${r.solvability_score >= 70 ? 'badge-success' : r.solvability_score >= 40 ? 'badge-warning' : 'badge-danger'}`}>{r.solvability_score}%</span> : '-' },
    {
      header: 'Actions',
      render: (r) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          style={{ padding: '3px 8px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          onClick={(e) => openEdit(r, e)}
          title="Modifier les informations et les accès du locataire"
        >
          <FiEdit2 size={12} /> Modifier
        </button>
      )
    }
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Locataires</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><FiPlus /> Nouveau locataire</button>
      </div>
      <div className="filters">
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <FiSearch style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-secondary)' }} />
          <input className="form-control" style={{ paddingLeft: 36 }} placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      {loading ? <div className="loading"><div className="spinner" /></div> : (
        <div className="card"><DataTable columns={columns} data={tenants} onRowClick={(r) => navigate(`/tenants/${r.id}`)} emptyMessage="Aucun locataire" /></div>
      )}

      {/* Modal Création */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Nouveau locataire" size="lg">
        <form onSubmit={handleCreate}>
          <div className="grid-2">
            <div className="form-group"><label>Prénom *</label><input className="form-control" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required /></div>
            <div className="form-group"><label>Nom *</label><input className="form-control" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required /></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Email</label><input type="email" className="form-control" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-group"><label>Téléphone</label><input className="form-control" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>Adresse de résidence</label><input className="form-control" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Quartier, Rue, Porte..." /></div>
          <div className="grid-2">
            <div className="form-group"><label>Profession</label><input className="form-control" value={form.occupation} onChange={e => setForm({ ...form, occupation: e.target.value })} /></div>
            <div className="form-group"><label>Employeur</label><input className="form-control" value={form.employer} onChange={e => setForm({ ...form, employer: e.target.value })} /></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Revenu mensuel (XOF)</label><input type="number" className="form-control" value={form.monthly_income} onChange={e => setForm({ ...form, monthly_income: e.target.value })} /></div>
            <div className="form-group"><label>Type pièce ID</label><input className="form-control" value={form.id_type} onChange={e => setForm({ ...form, id_type: e.target.value })} placeholder="CNI, Passeport, Nina..." /></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Numéro pièce ID</label><input className="form-control" value={form.id_number} onChange={e => setForm({ ...form, id_number: e.target.value })} /></div>
            <div className="form-group"><label>Contact d'urgence (Nom)</label><input className="form-control" value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
          </div>
          <div className="grid-2">
            <div className="form-group"><label>Téléphone d'urgence</label><input className="form-control" value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
            <div className="form-group"><label>Notes internes</label><input className="form-control" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          
          {/* Option création compte portail */}
          <div className="form-group" style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
              <input 
                type="checkbox" 
                checked={form.create_account} 
                onChange={e => setForm({ ...form, create_account: e.target.checked })} 
              />
              <span>Créer un compte portail locataire (accès au dashboard)</span>
            </label>
          </div>
          
          {form.create_account && (
            <div className="form-group" style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: 600 }}>Mot de passe initial pour le portail *</label>
              <input 
                type="password" 
                className="form-control" 
                value={form.password} 
                onChange={e => setForm({ ...form, password: e.target.value })} 
                required={form.create_account}
                placeholder="Mot de passe (min. 6 caractères)"
                minLength={6}
              />
              <small style={{ color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                Le locataire pourra se connecter à l'espace portail avec son email ({form.email || 'non renseigné'}) et ce mot de passe.
              </small>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary">
              {form.create_account ? 'Créer avec compte portail' : 'Créer le locataire'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Modification Locataire & Gestion Accès Portail */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Modifier le locataire & accès portail" size="lg">
        {selectedTenant && (
          <form onSubmit={handleUpdate}>
            <div className="grid-2">
              <div className="form-group"><label>Prénom *</label><input className="form-control" value={editForm.first_name} onChange={e => setEditForm({ ...editForm, first_name: e.target.value })} required /></div>
              <div className="form-group"><label>Nom *</label><input className="form-control" value={editForm.last_name} onChange={e => setEditForm({ ...editForm, last_name: e.target.value })} required /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label>Email *</label><input type="email" className="form-control" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} required /></div>
              <div className="form-group"><label>Téléphone</label><input className="form-control" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            </div>
            <div className="form-group"><label>Adresse de résidence</label><input className="form-control" value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} placeholder="Quartier, Rue, Porte..." /></div>
            <div className="grid-2">
              <div className="form-group"><label>Profession</label><input className="form-control" value={editForm.occupation} onChange={e => setEditForm({ ...editForm, occupation: e.target.value })} /></div>
              <div className="form-group"><label>Employeur</label><input className="form-control" value={editForm.employer} onChange={e => setEditForm({ ...editForm, employer: e.target.value })} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label>Revenu mensuel (XOF)</label><input type="number" className="form-control" value={editForm.monthly_income} onChange={e => setEditForm({ ...editForm, monthly_income: e.target.value })} /></div>
              <div className="form-group"><label>Type pièce ID</label><input className="form-control" value={editForm.id_type} onChange={e => setEditForm({ ...editForm, id_type: e.target.value })} placeholder="CNI, Passeport, Nina..." /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label>Numéro pièce ID</label><input className="form-control" value={editForm.id_number} onChange={e => setEditForm({ ...editForm, id_number: e.target.value })} /></div>
              <div className="form-group"><label>Contact d'urgence (Nom)</label><input className="form-control" value={editForm.emergency_contact_name} onChange={e => setEditForm({ ...editForm, emergency_contact_name: e.target.value })} /></div>
            </div>
            <div className="grid-2">
              <div className="form-group"><label>Téléphone d'urgence</label><input className="form-control" value={editForm.emergency_contact_phone} onChange={e => setEditForm({ ...editForm, emergency_contact_phone: e.target.value })} /></div>
              <div className="form-group">
                <label>Statut locataire</label>
                <select className="form-control" value={editForm.is_active ? 'true' : 'false'} onChange={e => setEditForm({ ...editForm, is_active: e.target.value === 'true' })}>
                  <option value="true">Actif</option>
                  <option value="false">Inactif</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notes internes</label>
              <textarea className="form-control" rows={2} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>

            {/* ════ SECTION ACCÈS PORTAIL LOCATAIRE ════ */}
            <div style={{
              marginTop: 20,
              padding: 18,
              borderRadius: 10,
              background: editForm.has_portal ? '#f0fdf4' : '#f8fafc',
              border: `1px solid ${editForm.has_portal ? '#bbf7d0' : '#e2e8f0'}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: editForm.has_portal ? '#166534' : '#1e293b' }}>
                  <FiShield color={editForm.has_portal ? '#16a34a' : '#64748b'} size={18} />
                  Accès Portail Locataire
                </h4>
                {editForm.has_portal ? (
                  <span className={`badge ${editForm.portal_active ? 'badge-success' : 'badge-warning'}`}>
                    {editForm.portal_active ? 'Compte portail actif' : 'Compte suspendu'}
                  </span>
                ) : (
                  <span className="badge badge-secondary">Non activé</span>
                )}
              </div>

              {/* Cas A: Aucun compte portail existant */}
              {!editForm.has_portal ? (
                <div>
                  <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 12px 0' }}>
                    Ce locataire n'a pas encore de compte utilisateur. Vous pouvez lui donner accès au portail pour lui permettre de consulter ses baux, paiements et quittances en ligne.
                  </p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={editForm.enable_portal_access}
                      onChange={e => setEditForm({ ...editForm, enable_portal_access: e.target.checked })}
                    />
                    <span>Activer et créer l'accès au portail locataire</span>
                  </label>

                  {editForm.enable_portal_access && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 600 }}>Mot de passe initial pour le portail *</label>
                      <input
                        type="password"
                        className="form-control"
                        placeholder="Définir un mot de passe (min. 6 caractères)"
                        value={editForm.password}
                        onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                        required={editForm.enable_portal_access}
                        minLength={6}
                      />
                      <small style={{ color: '#64748b', display: 'block', marginTop: 4 }}>
                        L'identifiant de connexion sera : <strong>{editForm.email || selectedTenant.email}</strong>
                      </small>
                    </div>
                  )}
                </div>
              ) : (
                /* Cas B: Compte portail existant */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={editForm.portal_active}
                        onChange={e => setEditForm({ ...editForm, portal_active: e.target.checked })}
                      />
                      <span>Autoriser la connexion au portail (compte actif)</span>
                    </label>
                  </div>

                  <div>
                    <label style={{ fontSize: 13, fontWeight: 600 }}>Changer / Réinitialiser le mot de passe</label>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Laisser vide pour conserver le mot de passe actuel"
                      value={editForm.password}
                      onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                      minLength={6}
                    />
                    <small style={{ color: '#64748b', display: 'block', marginTop: 4 }}>
                      Identifiant actuel : <strong>{selectedTenant.user_email || selectedTenant.email}</strong>. Saisissez un nouveau mot de passe uniquement pour le modifier.
                    </small>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={editLoading}>
                {editLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default TenantListPage;
